import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FeatureFlagsService} from "../../services/feature-flag/feature-flags.service";
import * as Highcharts from 'highcharts/highcharts';
import {UtilService} from "../../services/util/util.service";
import {ReportService} from "../../services/report/report.service";
import {ReportUtilService} from "../../services/report/report-util.service";
import {
  EmbeddedFieldModel,
  EmbeddedFieldCallbackFunction,
  EmbeddedFieldAutoUpdateFunction
} from "../../models/embedded-field/embedded-field.model";

// This is very similar to PortalChartComponent - perhaps they could be combined.
@Component({
  selector: 'sb-embedded-report',
  templateUrl: './embedded-report.component.html',
  styleUrls: ['./embedded-report.component.scss']
})
export class EmbeddedReportComponent implements EmbeddedFieldModel, OnInit, AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('json', {static: false}) jsonElement?: ElementRef;

  @ViewChild('chart', {static: false}) chartElement?: ElementRef;

  @Input()
  rendererProperties: any = {};

  @Input()
  Chart: string;

  @Input()
  options: any = null;

  debug: boolean = false;

  // Data for Highcharts is returned from the chart definition. You have to supply a series to start with or
  // Highcharts will not update correctly when new data is supplied, e.g. when a different chart is chosen in edit mode.
  data: { options?: any, data?: any[] } = {
    options: {
      series: [{
        data: []
      }]
    },
    data: []
  };

  // It is not possible to reset the options in the HighCharts component as they accumulate. Hence we have to destroy and
  // rebuild the component on every change. This flag tells angular which version of the highcharts component to display.
  toggleChart: boolean = false;

  // Highcharts Angular component settings.
  Highcharts = Highcharts;
  chartConstructor = 'chart'; // optional string, defaults to 'chart'
  updateFlag = false; // optional boolean, set to true when data updated

  @Input()
  initAngularComponent: EmbeddedFieldCallbackFunction;

  @Input()
  updateAngularComponent: EmbeddedFieldCallbackFunction;

  // Marked as @Input() so that it is visible on the knockout side as part of the web component.
  @Input()
  autoUpdateAngularComponent: EmbeddedFieldAutoUpdateFunction;

  // Key into the ReportService for drill down purposes
  reportComponentKey: string;

  constructor(private reportService: ReportService,
              private reportUtilService: ReportUtilService,
              private featureFlagsService: FeatureFlagsService,
              private utilService: UtilService) {
    // Build update method that binds 'this' to the correct value. If 'update' is called from another
    // context, e.g. Knockout calling a method on a web component, 'this' will be the HTMLElement
    // not the Angular component unless we do this bind.
    const that = this;

    this.initAngularComponent =  async function (current: any, eventName?: string, eventContext?: any) {
      await that.initImpl(current);
    };

    this.updateAngularComponent =  async function (current: any, eventName?: string, eventContext?: any) {
      await that.updateImpl(current);
    };

    this.autoUpdateAngularComponent =  function () {
      return false;
    };

    (window as any).HC = Highcharts;
    (window as any).Highcharts = Highcharts;
  }

  async initImpl(current: any) {
  }

  private getLocalized(f: any): string {
    return (f && f.i18nKey) ? f.i18nKey : f;
  }

  async updateImpl(current: any) {
    const params = {
      EffectsType: current.EffectsType(),
      AggregationType: current.AggregationType(),
      GroupType: current.GroupType(),
      PeriodType: current.PeriodType(),
      GroupByPeriodType: current.GroupByPeriodType(),
      StackByPeriodType: current.StackByPeriodType(),
      GroupByOrder: current.GroupByOrder(),
      StackByOrder: current.StackByOrder(),
      DurationType: current.DurationType(),
      // TODO: Should be renamed DecimalPlaces as it now also used for Percentage && Money Fields
      DurationDecimalPlaces: current.DurationDecimalPlaces(),
      PrimaryTable: current.PrimaryTable(),
      SecondaryTable: current.SecondaryTable(),
      PrimaryQuery: current.PrimaryQuery(),
      SecondaryQuery: current.SecondaryQuery(),
      PrimaryGroupBy: current.PrimaryGroupBy(),
      SecondaryGroupBy: current.SecondaryGroupBy(),
      PrimaryStackBy: current.PrimaryStackBy(),
      SecondaryStackBy: current.SecondaryStackBy(),
      PrimaryAggregationField: current.PrimaryAggregationField(),
      SecondaryAggregationField: current.SecondaryAggregationField(),
      PrimaryJoinField: current.PrimaryJoinField(),
      SecondaryJoinField: current.SecondaryJoinField(),
      UsePrimaryGroupBy: current.UsePrimaryGroupBy(),
      UsePrimaryStackBy: current.UsePrimaryStackBy(),
      UsePrimaryAggregationField: current.UsePrimaryAggregationField(),
      Name: current.Name(),
      Description: current.Description(),
      Title: current.Title(),
      LocalizedName: this.getLocalized(current.LocalizedName()),
      LocalizedDescription: this.getLocalized(current.LocalizedDescription()),
      LocalizedTitle: this.getLocalized(current.LocalizedTitle()),
      Advanced: current.Advanced(),
      Format: current.Format(),
      Data: current.Data(),
      GroupByFullRange: current.GroupByFullRange(),
      StackByFullRange: current.StackByFullRange(),
      TemplateCategory: current.TemplateCategory(),
      Template: current.Template(),
      Theme: current.Theme(),
      PostProcessing: current.PostProcessing(),
      Configuration: current.Configuration(),
      RecordLimit: current.RecordLimit(),
      AggregationOrderBy: current.AggregationOrderBy(),
      DisableDefaultGrouping: current.DisableDefaultGrouping(),
    }

    await this.renderChart(params);
  }


  async ngOnInit() {
    this.debug = this.featureFlagsService.isOn("report-debugging-display");

    this.reportComponentKey = this.utilService.generateUUID();
  }

  async ngAfterViewInit() {
    if (this.Chart || this.options) {
      this.utilService.tickThen(async () => {
      await this.renderChart();
    });
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (this.Chart || this.options) {
      await this.renderChart();
    }
  }

  ngOnDestroy(): void {
    this.reportService.removeReport(this.reportComponentKey);
  }

  chartInstance(chart) {
  }

  updateChange(event) {
  }

  debugStyle() {
    return this.debug ? '' : 'display: none';
  }

  private async renderChart(params: any = null) {

    if (this.Chart || params) {

      const requestOptions = {
        _request_type: "getReportContent",
        _chart_id: params ? params : this.Chart,
        _context: {}
      };

      try {
        // Call ReportController which in turn calls Report script library and returns HighCharts config
        const response = await (window as any).WS.controller("ReportController", requestOptions);

        const responseData = response.data || response;

        if (responseData && (responseData.error || responseData._error)) {
          console.error(responseData.error || responseData._error);
        } else {
          const chartContent = responseData;
          if (chartContent && typeof chartContent === "object" && chartContent.options && chartContent.data) {
            this.reifyFunctions(chartContent.options);

            // Supplying data explicitly in the chart definition
            if (chartContent.data && chartContent.data.length > 0) {
              chartContent.options.series = chartContent.data;
            }

            // Call the before function on the chart if it exists.
            if (chartContent.options.servicely && chartContent.options.servicely.before) {
              chartContent.options.servicely.before.call(chartContent.options, this.reportUtilService, Highcharts);
            }

            // Store the data - note that highcharts will combine these options with the existing ones so we have to
            // destroy the old component and build a new one.
            this.data = {
              options: chartContent.options,
              data: chartContent.data,
            };

            this.reportService.addReport(this.reportComponentKey, this.data);

            this.toggleChart = !this.toggleChart;
            this.updateFlag = true;
            this.updateDebugDisplay();
          }
        }
      } catch (e) {
        console.error('EmbeddedReportComponent.renderChart', e);
      }
    } else if (this.options) {
      // Store the data - the highcharts options are explicitly provided in the Angular component
      this.data = {
        options: this.options,
        data: [],
      };
      this.reportService.addReport(this.reportComponentKey, this.data);

      this.toggleChart = !this.toggleChart;
      this.updateFlag = true;
      this.updateDebugDisplay();
    }
  }

  private reifyFunctions(obj: any) {
    const that = this;
    Object.getOwnPropertyNames(obj).forEach(function (name) {
      const functionMarker = 'function:';
      const value = obj[name];
      if (typeof(value) === 'string' && value.substring(0, functionMarker.length) === functionMarker) {
        obj[name] = eval('(' + value.substring(functionMarker.length) + ')');
      } else if (value && typeof(value) === 'object') {
        that.reifyFunctions(value);
      } else if (value && Array.isArray(value)) {
        value.forEach(function(element) {
          that.reifyFunctions(element);
        })
      }
    });
  }

  private updateDebugDisplay() {
    if (this.jsonElement) {
      this.jsonElement.nativeElement.innerHTML = '';
      this.jsonElement.nativeElement.appendChild(document.createTextNode(JSON.stringify(this.data, null, 4)));
    }
  }

}
